import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, Divider, Alert, Grid, TextField,
  FormControlLabel, Checkbox, FormHelperText,
} from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';

import * as salesScheduleApi from '../../lib/api/salesSchedule';

/**
 * 영업 일정 등록·수정.
 *
 * 이 화면의 다른 일정은 시스템이 만들어 준다 — 기회 · 프로젝트 · 계산서에서 뽑아온다.
 * 여기서 만드는 것은 사람이 적어두는 일정이다. "다음 주 화요일 A사 방문" 같은 것.
 *
 * 공유는 부서 단위다. 공유해도 남이 고치지는 못하고 보기만 한다.
 */
const SalesScheduleFormDialog = ({ open, schedule, currentUsrUuid, onClose, onSaved }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [startDt, setStartDt] = useState('');
  const [endDt, setEndDt] = useState('');
  const [shareYn, setShareYn] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEdit = Boolean(schedule?.schdUuid);
  // 남이 공유한 일정은 열어서 볼 수는 있어도 고치지는 못한다.
  const isMine = !isEdit || schedule?.crtUsr === currentUsrUuid;

  useEffect(() => {
    if (!open) return;
    setTitle(schedule?.title || '');
    setContent(schedule?.content || '');
    setStartDt(schedule?.startDt || new Date().toISOString().split('T')[0]);
    setEndDt(schedule?.endDt || '');
    setShareYn(schedule?.shareYn === 'Y');
    setError(null);
  }, [open, schedule]);

  const problem = (() => {
    if (!title.trim()) return '제목을 입력하세요.';
    if (!startDt) return '시작일을 입력하세요.';
    if (endDt && endDt < startDt) return '종료일이 시작일보다 빠릅니다.';
    return '';
  })();

  const handleSave = async () => {
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: title.trim(),
        content: content.trim() || null,
        startDt,
        endDt: endDt || null,
        shareYn: shareYn ? 'Y' : 'N',
      };
      const res = isEdit
        ? await salesScheduleApi.editSalesSchedule(schedule.schdUuid, payload)
        : await salesScheduleApi.addSalesSchedule(payload);

      if (res.data?.result === 'success') {
        onSaved();
      } else {
        setError(res.data?.message || '저장하지 못했습니다.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || '저장하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await salesScheduleApi.removeSalesSchedule(schedule.schdUuid);
      if (res.data?.result === 'success') {
        onSaved();
      } else {
        setError(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || '삭제하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventNoteIcon color="primary" />
          <Typography variant="h6">{isEdit ? '일정 수정' : '일정 등록'}</Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {!isMine && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {schedule?.crtNm || '다른 사람'}님이 공유한 일정입니다. 보기만 할 수 있습니다.
          </Alert>
        )}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="제목" required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="예: A사 방문"
              disabled={!isMine}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" type="date" label="시작일" required
              value={startDt}
              onChange={(e) => setStartDt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!isMine}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" type="date" label="종료일"
              value={endDt}
              onChange={(e) => setEndDt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="비우면 하루짜리 일정입니다"
              disabled={!isMine}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth size="small" label="내용" multiline rows={3}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="선택"
              disabled={!isMine}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={shareYn}
                  onChange={(e) => setShareYn(e.target.checked)}
                  disabled={!isMine}
                />
              }
              label="우리 팀에 공유"
            />
            <FormHelperText sx={{ ml: 4, mt: -0.5 }}>
              같은 부서 사람들이 함께 봅니다. 고치고 지우는 것은 등록한 사람만 할 수 있습니다.
            </FormHelperText>
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Box>
          {isEdit && isMine && (
            <Button color="error" onClick={handleDelete} disabled={saving}>
              삭제
            </Button>
          )}
        </Box>
        <Box>
          <Button onClick={onClose} color="inherit" disabled={saving} sx={{ mr: 1 }}>
            닫기
          </Button>
          {isMine && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SalesScheduleFormDialog;
